<script setup lang="ts">
import { computed, watch, ref, onMounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useForm } from '@tanstack/vue-form'
//Components
import {
  CircleInfoIcon,
  ChevronRightIcon,
  FloppyDiskIcon,
  TrashCanIcon,
  XMarkIcon,
  CheckIcon,
} from 'Common/components/elements/icons/Icons'
//Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
//Utils
import { z } from 'zod'
//Types
import {
  AnnotationTypes,
  AnnotationModalTypes,
  DefectTypes,
  DefectStatusTypes,
  DefectStatusIndicatorTypes,
} from 'Modules/types/pages/imageReview/enums'
import { getDefectTypeSelectOptions } from './helperFunctions'
import { ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'

interface AnnotationModalDetail {
  title: string
  submitBtnTxt: string
  deleteBtnTxt: string
}

enum StagedAnnotationGroupingType {
  Suggestion = 'suggestion',
  UserGenerated = 'userGenerated',
  Mixed = 'mixed',
}

const store = useImageReviewStore()
const annotationsStore = store.annotationsStore
const {
  stagedAnnotations,
  groupedAnnotationsMap,
  groupAnnotations,
  isAllStagedAnnotationInSameGroup,
  groupedSuggestionsMap,
} = storeToRefs(annotationsStore)
const { clearStagedAnnotations, updateStagedAnnotationValues, unstageAnnotationByUuid } = annotationsStore
const {
  handleAnnotationDelete,
  handleAnnotationSubmit,
  handleRemoveGroupSubmit,
  handleGroupSubmit,
  handleAnnotationEditAndGroupSubmit,
} = store.serverStore

const firstStagedAnnotation = computed(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return undefined
  return stagedAnnotations.value[0]
})

const allStagedAnnotationsEqual = computed(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return false
  if (stagedAnnotations.value.length === 1) return true

  // Safety check for first annotation
  const first = firstStagedAnnotation.value
  if (!first) return false

  return stagedAnnotations.value.every(
    (annotation) =>
      annotation.defectType === first.defectType &&
      annotation.defectStatus === first.defectStatus &&
      annotation.annotationGroup === first.annotationGroup
  )
})

//TODO can this be simplified? Logic for getting first staged or allstagedAnnotationsEqual values is redundant.
const defectType = computed<DefectTypes | undefined>(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return null
  if (stagedAnnotations.value.length === 1 || allStagedAnnotationsEqual.value) {
    return firstStagedAnnotation.value?.defectType
  }
  return null
})

const defectStatus = computed<DefectStatusTypes | undefined>(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return null
  if (stagedAnnotations.value.length === 1 || allStagedAnnotationsEqual.value) {
    return firstStagedAnnotation.value?.defectStatus
  }
  return null
})

const defectComment = computed<string | undefined>(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return null
  if (stagedAnnotations.value.length === 1 || allStagedAnnotationsEqual.value) {
    return stagedAnnotations.value[0]?.defectComment
  }
  return null
})

const grouped = computed<boolean>(() => {
  if (isAllStagedAnnotationInSameGroup.value === true) return true
  if (groupAnnotations.value === true) return true
  return false
})

const typeSelectRef = ref(null)
const focusTypeIfEmpty = () => {
  if (!defectType.value && typeSelectRef.value) {
    nextTick(() => {
      typeSelectRef.value.focus()
    })
  }
}

const createFormSchema = (allEqual: boolean) => {
  return z.object({
    grouped: z.boolean().nullable().optional(),
    type: z
      .preprocess((value) => value ?? '', z.string()) // Convert null/undefined to empty string
      .superRefine((value, ctx) => {
        if (!value.trim()) {
          if (!allEqual && form.state.values.grouped === true) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Type must be updated to unify annotations.',
            })
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'This field is required.',
            })
          }
        }
      }),
    status: z
      .preprocess((value) => value ?? '', z.string()) // Convert null/undefined to empty string
      .superRefine((value, ctx) => {
        if (!value.trim()) {
          if (!allEqual && form.state.values.grouped === true) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Status must be updated to unify annotations.',
            })
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'This field is required.',
            })
          }
        }
      }),
    comment: z.string().nullable().optional(),
  })
}

const submitAction = async (): Promise<void> => {
  if (!stagedAnnotations.value) return

  //TODO remove when skimming down staged annotations
  //TODO improve typing in form so that they don't have to be casted
  const updatePayload: any = {
    defectType: form.state.values.type as DefectTypes,
    defectStatus: form.state.values.status as DefectStatusTypes,
    defectComment: form.state.values.comment,
  }


  if (showSuggestionButtons.value) {
    updatePayload.annotationStatus = 'Confirmed'
  }

  await updateStagedAnnotationValues(updatePayload)

  await nextTick()

  if (showSuggestionButtons.value) {
    stagedAnnotations.value.forEach((annotation, index) => {
      if (annotation.annotationType === AnnotationTypes.Suggestion) {
        const updatedAnnotation = {
          ...annotation,
          annotationStatus: 'Confirmed',
          defectType: form.state.values.type as DefectTypes,
          defectStatus: form.state.values.status as DefectStatusTypes,
          defectComment: form.state.values.comment,
        }

        stagedAnnotations.value[index] = updatedAnnotation
      }
    })
  }

  const isNewlyGrouped = form.state.values.grouped === true && isAllStagedAnnotationInSameGroup.value === false
  const isNewlyUngrouped = form.state.values.grouped === false && isAllStagedAnnotationInSameGroup.value === true

  if (isNewlyGrouped) {
    handleGroupSubmit()
  } else if (allStagedAnnotationsEqual.value === true) {
    handleAnnotationSubmit(modalType.value)
  } else {
    handleAnnotationEditAndGroupSubmit()
  }

  if (discrepanciesEditedOutsideOfGroup.value.length > 0 || isNewlyUngrouped) {
    handleRemoveGroupSubmit()
  }

  closeDefectDetail()
}

const deleteAction = (): void => {
  handleAnnotationDelete(modalType.value)
  closeDefectDetail()
}

// TODO Skim down staged annotations as they no longer need to contain
// all of the image annotation information.
// We can just store the annotation identifier (uuid).
// useForm hook holds a temporary state that doesn't impact
// image annotation state. We originally cloned the entire annotation
// object in staged annotations as the original DefectDetail implementation
// did update the annotation objects state prior to persisting to database.
// TODO add ticket to comment and reduce comment detail.
// TODO remove accompanying comment from annotationsStore
const form = useForm({
  onSubmit: submitAction,
  defaultValues: {
    grouped: grouped.value,
    type: defectType.value,
    status: defectStatus.value ?? 'requires_inspection',
    comment: defectComment.value,
  },
  validators: {
    onMount: createFormSchema(allStagedAnnotationsEqual.value),
    onChange: createFormSchema(allStagedAnnotationsEqual.value),
    onBlur: createFormSchema(allStagedAnnotationsEqual.value),
  },
})

const formIsGroupedValue = form.useStore((state) => state.values.grouped)
const canSubmit = form.useStore((state) => state.canSubmit)
const isValid = form.useStore((state) => state.isValid)

const defectTypeOptions = getDefectTypeSelectOptions()

const addAnnotationModalDetails: AnnotationModalDetail = {
  title: 'Add New Discrepancy',
  submitBtnTxt: 'Save',
  deleteBtnTxt: null,
}
const editAnnotationModalDetails: AnnotationModalDetail = {
  title: 'Edit Existing Discrepancy',
  submitBtnTxt: 'Update',
  deleteBtnTxt: 'Delete',
}
const editSuggestionAnnotationModalDetails: AnnotationModalDetail = {
  title: 'Edit Existing Discrepancy',
  submitBtnTxt: 'Update',
  deleteBtnTxt: 'Revert',
}
const suggestionAnnotationModalDetails: AnnotationModalDetail = {
  title: 'Confirm Discrepancy Suggestion',
  submitBtnTxt: 'Confirm',
  deleteBtnTxt: 'Reject',
}
const multiEditModalDetails: AnnotationModalDetail = {
  title: 'Edit Multiple Discrepancies',
  submitBtnTxt: 'Save/Confirm',
  deleteBtnTxt: 'Delete/Reject',
}

const modalDetailMapping = {
  add: addAnnotationModalDetails,
  edit: editAnnotationModalDetails,
  editsuggestion: editSuggestionAnnotationModalDetails,
  suggestion: suggestionAnnotationModalDetails,
  multiEdit: multiEditModalDetails,
}

function getStagedAnnotationsTypes(): string | undefined {
  if (!stagedAnnotations.value) return null

  let hasSuggestedAnnotations = false
  let hasUserCreatedAnnotations = false

  stagedAnnotations.value?.some((annotation) => {
    if (annotation?.annotationType === AnnotationTypes.Suggestion) {
      hasSuggestedAnnotations = true
    } else {
      hasUserCreatedAnnotations = true
    }

    // Early exit if both are found
    return hasSuggestedAnnotations && hasUserCreatedAnnotations
  })

  // Determine the result based on the flags
  if (hasSuggestedAnnotations && hasUserCreatedAnnotations) return StagedAnnotationGroupingType.Mixed
  if (hasSuggestedAnnotations) return StagedAnnotationGroupingType.Suggestion
  if (hasUserCreatedAnnotations) return StagedAnnotationGroupingType.UserGenerated
  return null
}

const annotationTypeLabel = computed<string>(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return ''

  let str

  const isMultipleStagedAnnotations = stagedAnnotations.value.length > 1
  const isSuggestion = firstStagedAnnotation.value?.annotationType === AnnotationTypes.Suggestion
  const isConfirmed = firstStagedAnnotation.value?.isAnnotation
  const stagedAnnotationsType = getStagedAnnotationsTypes()

  switch (true) {
    case isMultipleStagedAnnotations && stagedAnnotationsType === StagedAnnotationGroupingType.Mixed:
      str = 'User generated and Suggested by Damage Detection'
      break
    case !isSuggestion:
      str = 'User generated'
      break
    case !isConfirmed:
      str = 'Suggested By Damage Detection'
      break
    default:
      str = 'Suggestion Confirmed By User'
  }
  return str
})

const modalType = computed(() => {
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return
  if (stagedAnnotations.value.length > 1) {
    return AnnotationModalTypes.MultiEdit
  }

  const first = firstStagedAnnotation.value
  if (!first) return

  let typeVal

  switch (true) {
    case first.isAnnotation && first?.isTemp:
      typeVal = AnnotationModalTypes.Add
      break
    case first?.isAnnotation && first?.annotationType !== 'Suggestion':
      typeVal = AnnotationModalTypes.Edit
      break
    case first?.annotationType === 'Suggestion' && first?.annotationStatus === 'Confirmed':
      typeVal = AnnotationModalTypes.EditSuggestion
      break
    case first?.annotationType === 'Suggestion':
      typeVal = AnnotationModalTypes.Suggestion
      break
  }
  return typeVal
})

const showSuggestionButtons = computed<boolean>(() => {
  const annotationTypes = getStagedAnnotationsTypes()
  if (
    annotationTypes === StagedAnnotationGroupingType.Mixed ||
    annotationTypes === StagedAnnotationGroupingType.UserGenerated
  )
    return false
  return true
})

const title = computed<string>(() => {
  if (stagedAnnotations.value && stagedAnnotations.value.length > 0) return modalDetails.value.title
  return ''
})
const commentPlaceHolder = computed<string>(() => {
  if (!stagedAnnotations.value) return ''

  if (stagedAnnotations.value.length > 1) {
    return 'Comment on all selected discrepancies...'
  }
  if (stagedAnnotations.value.length === 1) {
    return `Comment on discrepancy ${stagedAnnotations.value[0].identifier}...`
  }
  return ''
})
const defectTypeErrorClass = computed<string>(() => (!defectType.value ? 'error' : ''))
const defectStatusErrorClass = computed<string>(() => (!defectStatus.value ? 'error' : ''))
const modalDetails = computed<AnnotationModalDetail>(() => {
  const val = modalDetailMapping[modalType.value] || ({} as AnnotationModalDetail)
  return val
})
const allowEdit = computed<boolean>(() => {
  if (stagedAnnotations.value && stagedAnnotations.value.length > 0) return true
  return false
})
const shouldShowCancelButton = computed<boolean>(() => {
  return modalType.value !== AnnotationModalTypes.Add
})
const showDiscrepanciesList = computed<boolean>(() => {
  if (!stagedAnnotations.value) return false
  if (stagedAnnotations.value.length === 1 && stagedAnnotations.value[0].isTemp) return false
  return true
})

const discrepanciesEditedOutsideOfGroup = computed<Array<ImageAnnotationType>>(() => {
  const val: ImageAnnotationType[] = [] // Typed array for safety

  // Safety check added to prevent crash
  if (!stagedAnnotations.value) return val

  stagedAnnotations.value.forEach((annotation) => {
    if (annotation?.annotationGroup !== undefined) {
      let annotationsInGroup

      if (annotation.isAnnotation) {
        annotationsInGroup = groupedAnnotationsMap.value[annotation.annotationGroup]
      } else {
        // It is a suggestion, check the Suggestions Map
        annotationsInGroup = groupedSuggestionsMap.value[annotation.annotationGroup]
      }

      if (!annotationsInGroup) return

      const stagedContainsAllInGroup = annotationsInGroup.every((groupedAnnotation) =>
        stagedAnnotations.value.some(
          (stagedAnnotation) => stagedAnnotation.annotationUuid === groupedAnnotation.annotationUuid
        )
      )
      if (!stagedContainsAllInGroup) {
        val.push(annotation)
      }
    }
  })
  return val
})

const discrepanciesEditedOutsideOfGroupText = computed<string>(() => {
  return discrepanciesEditedOutsideOfGroup.value.map((annotation) => annotation.identifier).join(', ')
})

const allowGrouping = computed<boolean>(() => {
  // Safety check added to prevent crash
  if (!stagedAnnotations.value || stagedAnnotations.value.length === 0) return false

  const isAllValidForGrouping = stagedAnnotations.value.every((annotation) => annotation.isTemp !== true)
  return isAllValidForGrouping
})

const handleStageAnnotationPillClick = (annotation) => {
  unstageAnnotationByUuid(annotation.annotationUuid)
}

const cancelAction = (): void => {
  closeDefectDetail()
}

const closeDefectDetail = (): void => {
  groupAnnotations.value = false
  clearStagedAnnotations()
}

watch([defectType, defectStatus, defectComment, allStagedAnnotationsEqual, isAllStagedAnnotationInSameGroup], () => {
  const schema = createFormSchema(allStagedAnnotationsEqual.value)
  form.update({
    onSubmit: submitAction,
    validators: {
      onMount: schema,
      onChange: schema,
      onBlur: schema,
    },
  })
  form.reset({
    grouped: grouped.value,
    type: defectType.value,
    status: defectStatus.value ?? 'requires_inspection',
    comment: defectComment.value,
  })
  form.validateAllFields('mount')
  focusTypeIfEmpty()
})

watch([formIsGroupedValue], () => {
  const schema = createFormSchema(allStagedAnnotationsEqual.value)
  form.update({
    onSubmit: submitAction,
    validators: {
      onMount: schema,
      onChange: schema,
      onBlur: schema,
    },
  })
  form.validateAllFields('mount')
})

onMounted(() => {
  focusTypeIfEmpty()
})
</script>

<template>
  <div class="h-full w-full flex flex-col bg-white">
    <div class="px-2 py-1 bg-stone-300 drop-shadow flex-none z-10">
      <div class="w-full flex justify-between items-center gap-2">
        <div class="flex items-center gap-1">
          <el-tooltip placement="bottom" content="cancel" :show-after="500">
            <el-button size="small" @click="cancelAction">
              <ChevronRightIcon class="rotate-180" />
            </el-button>
          </el-tooltip>

          <el-tooltip placement="bottom" :content="modalDetails.submitBtnTxt" :show-after="500">
            <el-button
              type="primary"
              size="small"
              :disabled="(showSuggestionButtons ? !isValid : !canSubmit) || !allowEdit"
              @click="showSuggestionButtons ? submitAction() : form.handleSubmit()"
            >
              <template v-if="!showSuggestionButtons">
                <FloppyDiskIcon />
              </template>
              <template v-else>
                <CheckIcon />
              </template>
            </el-button>
          </el-tooltip>

          <template v-if="shouldShowCancelButton">
            <el-tooltip placement="bottom" :content="modalDetails.deleteBtnTxt" :show-after="500">
              <el-button type="danger" size="small" :disabled="!allowEdit" @click="deleteAction">
                <template v-if="!showSuggestionButtons">
                  <TrashCanIcon />
                </template>
                <template v-else>
                  <XMarkIcon />
                </template>
              </el-button>
            </el-tooltip>
          </template>
        </div>

        <div class="flex flex-col items-end min-w-0">
          <span class="text-md font-medium truncate w-full text-right">{{ title }}</span>
          <span class="text-xs font-thin truncate w-full text-right">{{
            stagedAnnotations && stagedAnnotations.length === 1 ? stagedAnnotations[0].identifier : ''
          }}</span>
        </div>
      </div>
    </div>

    <div class="relative w-full flex-1 overflow-y-auto">
      <div class="relative h-full w-full flex flex-col bg-white">
        <div class="bg-sky-100 text-sm text-slate-600 drop-shadow sticky top-0 z-10">
          {{ annotationTypeLabel }}
        </div>

        <div
          v-if="discrepanciesEditedOutsideOfGroup.length > 0"
          class="p-2 bg-amber-100 text-sm text-slate-600 drop-shadow"
        >
          The discrepancie(s) {{ discrepanciesEditedOutsideOfGroupText }} are being edited outside of their group.<br />
          If the details differ from those in the group they will be removed from their respective groups.
        </div>

        <template v-if="showDiscrepanciesList">
          <div class="pt-4 pl-3 flex justify-start items-start flex-col">
            <div class="font-extralight mb-2">Discrepancies</div>
            <div class="flex flex-wrap gap-y-2 max-h-32 overflow-y-auto pr-2">
              <template v-for="annotation in stagedAnnotations" :key="annotation.annotationUuid">
                <span class="bg-neutral-200 py-2 px-3 mr-2 rounded-3xl flex justify-between items-center">
                  <span class="mr-1 text-neutral-600">{{ annotation.isTemp ? 'New' : annotation.identifier }}</span>
                  <el-button size="small" round @click="() => handleStageAnnotationPillClick(annotation)">
                    <XMarkIcon />
                  </el-button>
                </span>
              </template>
            </div>
          </div>
          <el-divider />
        </template>
        <div v-else class="py-4"></div>

        <form>
          <template v-if="stagedAnnotations && stagedAnnotations.length > 1">
            <div class="w-full px-3 flex items-center justify-end">
              <form.Field name="grouped">
                <template #default="{ field, state }">
                  <el-form-item id="group-form-item" label="Group Discrepancies" label-width="12em" class="w-full">
                    <el-switch
                      :model-value="state.value"
                      size="large"
                      :disabled="!allowGrouping"
                      @change="(e) => field.handleChange(e)"
                    />
                  </el-form-item>
                </template>
              </form.Field>
              <span class="justify-self-end flex items-center mb-4">
                <el-popover
                  placement="right"
                  title="Group Discrepancies"
                  :width="325"
                  trigger="hover"
                  content="Group discrepancies into a single item. All discrepancy types, severities, and comments must match in order to group them."
                >
                  <template #reference>
                    <CircleInfoIcon class="p-2 text-sky-400" />
                  </template>
                </el-popover>
              </span>
            </div>
          </template>
          <div class="w-full px-3 mb-4 flex justify-end">
            <form.Field name="type">
              <template #default="{ field, state }">
                <el-form-item label="Type" label-width="4em" class="w-full" :error="state.meta.errors[0]">
                  <el-select
                    ref="typeSelectRef"
                    :model-value="state.value"
                    class="defect-select"
                    :class="defectTypeErrorClass"
                    filterable
                    placeholder="Select Type"
                    @change="(e) => field.handleChange(e)"
                  >
                    <el-option-group v-for="group in defectTypeOptions" :key="group.label" :label="group.label">
                      <el-option
                        v-for="item in group.options"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      />
                    </el-option-group>
                  </el-select>
                </el-form-item>
              </template>
            </form.Field>
            <span class="ml-1">*</span>
            <el-popover
              placement="right"
              title="Type"
              :width="325"
              trigger="hover"
              content="The type of damage noted within discrepancy bounds."
            >
              <template #reference>
                <CircleInfoIcon class="p-2 text-sky-400" />
              </template>
            </el-popover>
          </div>
          <div class="w-full px-3 flex justify-end">
            <form.Field name="status">
              <template #default="{ field, state }">
                <el-form-item label="Status" label-width="4em" class="w-full" :error="state.meta.errors[0]">
                  <el-select
                    :model-value="state.value"
                    class="defect-select"
                    :class="defectStatusErrorClass"
                    filterable
                    placeholder="Select Severity"
                    @change="(e) => field.handleChange(e)"
                  >
                    <el-option
                      v-for="(value, key) in DefectStatusTypes"
                      :key="key"
                      :label="`${DefectStatusIndicatorTypes[key]} ${value}`"
                      :value="key"
                    />
                  </el-select>
                </el-form-item>
              </template>
            </form.Field>
            <span class="ml-1">*</span>
            <el-popover
              placement="right"
              title="Type"
              :width="250"
              trigger="hover"
              content="The status of the selected discrepancy."
            >
              <template #reference>
                <CircleInfoIcon class="p-2 text-sky-400" />
              </template>
            </el-popover>
          </div>
          <el-divider />
          <div class="px-4">
            <el-form-item>
              <form.Field name="comment">
                <template #default="{ field, state }">
                  <el-input
                    :model-value="state.value"
                    maxlength="500"
                    :placeholder="commentPlaceHolder"
                    show-word-limit
                    type="textarea"
                    :rows="10"
                    @input="(e) => field.handleChange(e)"
                  />
                </template>
              </form.Field>
            </el-form-item>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.defect-select.error .el-input__wrapper {
  box-shadow: 0 0 0 1px red;
}
.el-button {
  margin: 0;
  margin-left: 0;
  margin-right: 0.25em;
}

.el-button + .el-button {
  margin-left: 0;
}

::v-deep .el-select-group__title {
  background-color: #cbd5e1;
  font: size 0.8rem;
  color: black;
}
</style>

<style>
#group-form-item {
  display: flex;
  align-items: center;
}

#group-form-item .el-form-item__label {
  justify-content: flex-start;
  padding-left: 1em;
}

#group-form-item .el-form-item__content {
  justify-content: flex-end;
  margin-right: 1em;
}
</style>
